/* Dark-earth globe material.

   react-globe.gl builds the globe with a textured MeshPhongMaterial; we hand it
   our own so we control the mood. Goals:
     - very dark land/ocean (bg #0a0a0a vibe) so data overlays pop
     - a faint cyan emissive lift so the planet body never goes pure-black
     - keep the night-lights texture as a subtle city-glow detail
     - a specular sheen on oceans for a glassy terminal feel

   We load textures lazily; until they arrive the material still renders as a
   tinted dark sphere, so there is never a blank globe.
*/
import * as THREE from "three"
import { GLOBE_PALETTE, hexToRgb01 } from "../globe-colors"

const NIGHT_TEX = "//unpkg.com/three-globe/example/img/earth-night.jpg"
const TOPO_TEX = "//unpkg.com/three-globe/example/img/earth-topology.png"

export function buildGlobeMaterial(): THREE.MeshPhongMaterial {
  const [ar, ag, ab] = hexToRgb01(GLOBE_PALETTE.accent)
  const mat = new THREE.MeshPhongMaterial({
    color: new THREE.Color(0x0a1318), // near-bg dark base tint
    emissive: new THREE.Color(ar * 0.05, ag * 0.06, ab * 0.07),
    specular: new THREE.Color(0x16323a),
    shininess: 12,
    transparent: false,
  })

  const loader = new THREE.TextureLoader()
  loader.setCrossOrigin("anonymous")

  // Night lights → emissive map (city glow), but dimmed so it reads as detail.
  loader.load(NIGHT_TEX, (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace
    mat.map = tex
    mat.emissiveMap = tex
    mat.emissive = new THREE.Color(ar * 0.18, ag * 0.2, ab * 0.24)
    mat.emissiveIntensity = 0.55
    mat.needsUpdate = true
  })

  // Topology as a bump map for subtle terrain relief on the limb.
  loader.load(TOPO_TEX, (tex) => {
    mat.bumpMap = tex
    mat.bumpScale = 6
    mat.needsUpdate = true
  })

  return mat
}
